/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useRef } from "react";
import { ComponentData, PageData, BuilderState } from "@/types/builder";
import { generateCSS, generateHTML } from "@/utils/cssGenerator";
import { ensureComponentDefaults } from "@/utils/componentDefaults";
import { updatePage as updatePageApi } from "@/services/pagesApi";
import { toast } from "react-toastify";
import { Settings } from "lucide-react";
import { Project } from "@/types/project";

export const useBuilder = () => {
  const defaultPage = {
    _id: "page-1",
    title: "Untitled Page",
    page_type: "",
    settings: {},
    components: [],
    created_at: new Date(),
    updated_at: new Date(),
    slug: "",
    status: "",
  };

  const [state, setState] = useState<BuilderState>({
    currentPage: defaultPage,
    allPages: [defaultPage],
    selectedComponent: null,
    isDragging: false,
    draggedComponent: null,
    history: [],
    historyIndex: -1,
  });

  const generateId = useCallback(() => {
    return `component-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const addComponent = useCallback(
    (
      component: ComponentData,
      index?: number,
      globalButtonStyle?: Record<string, any>,
    ) => {
      setState((prev) => {
        if (!prev.currentPage) return prev;

        const withDefaults = ensureComponentDefaults(
          component,
          globalButtonStyle,
        );
        const newComponent = JSON.parse(
          JSON.stringify({ ...withDefaults, id: generateId() }),
        );
        const newComponents = [...prev.currentPage.components];

        if (index !== undefined) {
          newComponents.splice(index, 0, newComponent);
        } else {
          newComponents.push(newComponent);
        }

        const updatedPage = {
          ...prev.currentPage,
          components: newComponents,
          updatedAt: new Date(),
        };

        return {
          ...prev,
          currentPage: updatedPage,
          selectedComponent: newComponent,
        };
      });
    },
    [generateId],
  );

  const duplicateComponent = useCallback(
    (component: ComponentData, afterIndex: number) => {
      setState((prev) => {
        if (!prev.currentPage) return prev;

        const deepClone = JSON.parse(JSON.stringify(component));
        const assignNewIds = (comp: any): any => {
          comp.id = `component-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          if (comp.children) {
            comp.children = comp.children.map(assignNewIds);
          }
          return comp;
        };
        const duplicate = assignNewIds(deepClone);

        const newComponents = [...prev.currentPage.components];
        newComponents.splice(afterIndex + 1, 0, duplicate);

        const updatedPage = {
          ...prev.currentPage,
          components: newComponents,
          updatedAt: new Date(),
        };

        return {
          ...prev,
          currentPage: updatedPage,
          selectedComponent: duplicate,
        };
      });
    },
    [],
  );

  const updateComponent = useCallback(
    (id: string, updates: Partial<ComponentData>) => {
      setState((prev) => {
        if (!prev.currentPage) return prev;

        const updateComponentRecursive = (
          components: ComponentData[],
        ): ComponentData[] => {
          return components.map((component) => {
            if (component.id === id) {
              return { ...component, ...updates };
            }
            if (component.children) {
              return {
                ...component,
                children: updateComponentRecursive(component.children),
              };
            }
            return component;
          });
        };

        const updatedComponents = updateComponentRecursive(
          prev.currentPage.components,
        );
        const updatedPage = {
          ...prev.currentPage,
          components: updatedComponents,
          updatedAt: new Date(),
        };

        return {
          ...prev,
          currentPage: updatedPage,
          selectedComponent:
            prev.selectedComponent?.id === id
              ? { ...prev.selectedComponent, ...updates }
              : prev.selectedComponent,
        };
      });
    },
    [],
  );

  const deleteComponent = useCallback((id: string) => {
    setState((prev) => {
      if (!prev.currentPage) return prev;

      const deleteComponentRecursive = (
        components: ComponentData[],
      ): ComponentData[] => {
        return components.filter((component) => {
          if (component.id === id) return false;
          if (component.children) {
            component.children = deleteComponentRecursive(component.children);
          }
          return true;
        });
      };

      const updatedComponents = deleteComponentRecursive(
        prev.currentPage.components,
      );
      const updatedPage = {
        ...prev.currentPage,
        components: updatedComponents,
        updatedAt: new Date(),
      };

      // toast.success("Component deleted");

      return {
        ...prev,
        currentPage: updatedPage,
        selectedComponent:
          prev.selectedComponent?.id === id ? null : prev.selectedComponent,
      };
    });
  }, []);

  const selectComponent = useCallback((component: ComponentData | null) => {
    setState((prev) => ({ ...prev, selectedComponent: component }));
  }, []);

  const exportAsJSON = useCallback(() => {
    if (!state.currentPage) {
      toast.error("No page to export");
      return;
    }

    const { _id, project_id, ...pageWithoutIds } = state.currentPage as any;

    const cleanPage = {
      ...pageWithoutIds,
      components: state.currentPage.components,
      settings: state.currentPage.settings || {},
    };

    const dataStr = JSON.stringify(cleanPage, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

    const exportFileDefaultName = `${state.currentPage.title
      .replace(/\s+/g, "-")
      .toLowerCase()}.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();

    toast.success("Page exported as JSON!");
  }, [state.currentPage]);

  const importFromJSON = useCallback((jsonData: string) => {
    try {
      const pageData = JSON.parse(jsonData);

      // Generate new IDs for all components
      const regenerateIds = (components: any[]): ComponentData[] => {
        return components.map((comp) => ({
          ...comp,
          id: `component-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          children: comp.children ? regenerateIds(comp.children) : undefined,
        }));
      };

      // Remove any existing IDs and regenerate
      const {
        _id,
        project_id,
        page_settings,
        settings: importedSettings,
        ...cleanPage
      } = pageData;

      // Support both keys for backward compatibility, prefer settings
      const mergedSettings = importedSettings || page_settings || {};

      setState((prev) => ({
        ...prev,
        currentPage: {
          ...prev.currentPage,
          ...cleanPage,
          _id: prev.currentPage?._id, // Keep current page ID
          project_id: prev.currentPage?.project_id, // Keep current project ID
          components: regenerateIds(pageData.components || []),
          settings: mergedSettings,
          updated_at: new Date(),
        },
        selectedComponent: null,
      }));
      toast.success("Page imported successfully!");
    } catch (error) {
      toast.error("Invalid JSON format");
    }
  }, []);

  const exportAsCSS = useCallback(() => {
    if (!state.currentPage) {
      toast.error("No page to export");
      return;
    }

    const css = generateCSS(
      state.currentPage.components,
      state.currentPage.title,
    );
    const dataUri = "data:text/css;charset=utf-8," + encodeURIComponent(css);

    const exportFileDefaultName = `${state.currentPage.title
      .replace(/\s+/g, "-")
      .toLowerCase()}.css`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();

    toast.success("CSS exported successfully!");
  }, [state.currentPage]);

  const exportAsHTML = useCallback(() => {
    if (!state.currentPage) {
      toast.error("No page to export");
      return;
    }

    const html = generateHTML(
      state.currentPage.components,
      state.currentPage.title,
    );
    const dataUri = "data:text/html;charset=utf-8," + encodeURIComponent(html);

    const exportFileDefaultName = `${state.currentPage.title
      .replace(/\s+/g, "-")
      .toLowerCase()}.html`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();

    toast.success("HTML exported successfully!");
  }, [state.currentPage]);

  const handlePageNameChange = (name: string) => {
    setState((prev) => {
      if (!prev.currentPage) return prev;

      return {
        ...prev,
        currentPage: {
          ...prev.currentPage,
          title: name,
          updatedAt: new Date(),
        },
      };
    });
  };

  const reorderComponent = useCallback((fromIndex: number, toIndex: number) => {
    setState((prev) => {
      if (!prev.currentPage) return prev;

      const newComponents = [...prev.currentPage.components];
      const [movedComponent] = newComponents.splice(fromIndex, 1);
      newComponents.splice(toIndex, 0, movedComponent);

      const updatedPage = {
        ...prev.currentPage,
        components: newComponents,
        updatedAt: new Date(),
      };

      // toast.success("Component reordered");

      return {
        ...prev,
        currentPage: updatedPage,
      };
    });
  }, []);

  const updatePageSettings = useCallback((settings: PageData["settings"]) => {
    setState((prev) => {
      if (!prev.currentPage) return prev;

      return {
        ...prev,
        currentPage: {
          ...prev.currentPage,
          settings: { ...prev.currentPage.settings, ...settings },
          updatedAt: new Date(),
        },
      };
    });
  }, []);

  const updatePageData = useCallback((updates: Partial<PageData>) => {
    setState((prev) => {
      if (!prev.currentPage) return prev;

      return {
        ...prev,
        currentPage: {
          ...prev.currentPage,
          ...updates,
          updatedAt: new Date(),
        },
      };
    });
  }, []);

  const clearPage = useCallback(() => {
    setState((prev) => {
      if (!prev.currentPage) return prev;

      return {
        ...prev,
        currentPage: {
          ...prev.currentPage,
          components: [],
          updatedAt: new Date(),
        },
        selectedComponent: null,
      };
    });
    toast.success("Page cleared");
  }, []);

  const createNewPage = useCallback(
    (title: string = "New Page", pageType: any) => {
      const newPage: PageData = {
        _id: `page-${Date.now()}`,
        title,
        page_type: pageType,
        components: [],
        created_at: new Date(),
        updated_at: new Date(),
        slug: "",
        settings: {},
        status: "published",
      };

      setState((prev) => ({
        ...prev,
        currentPage: newPage,
        allPages: [...prev.allPages, newPage],
        selectedComponent: null,
      }));

      toast.success("New page created");
      return newPage;
    },
    [],
  );

  const loadPage = useCallback((pageId: string) => {
    setState((prev) => {
      const page = prev.allPages.find((p) => p._id === pageId);
      if (!page) {
        // toast.error("Page not found");
        return prev;
      }

      return {
        ...prev,
        currentPage: page,
        selectedComponent: null,
      };
    });
  }, []);

  const deletePage = useCallback((pageId: string) => {
    setState((prev) => {
      const updatedPages = prev.allPages.filter((p) => p._id !== pageId);

      if (updatedPages.length === 0) {
        toast.error("Cannot delete the last page");
        return prev;
      }

      const newCurrentPage =
        prev.currentPage?._id === pageId ? updatedPages[0] : prev.currentPage;

      toast.success("Page deleted");

      return {
        ...prev,
        allPages: updatedPages,
        currentPage: newCurrentPage,
        selectedComponent: null,
      };
    });
  }, []);

  const updatePage = useCallback(
    (pageId: string, updates: Partial<PageData>) => {
      setState((prev) => {
        const updatedPages = prev.allPages.map((page) =>
          page._id === pageId
            ? { ...page, ...updates, updatedAt: new Date() }
            : page,
        );

        const updatedCurrentPage =
          prev.currentPage?._id === pageId
            ? { ...prev.currentPage, ...updates, updatedAt: new Date() }
            : prev.currentPage;

        return {
          ...prev,
          allPages: updatedPages,
          currentPage: updatedCurrentPage,
        };
      });
    },
    [],
  );

  const savePage = useCallback(async () => {
    if (!state.currentPage) {
      toast.error("No page to save");
      return;
    }

    // console.log(state.currentPage["status"]);

    try {

      if (state.currentPage.components[0].type == "offcanvas-settings") {

        delete state.currentPage?.settings?.pageBackground;
      }

      if (state.currentPage["status"] != "published") {
        await updatePageApi(state.currentPage._id, {
          title: state.currentPage.title,
          components: state.currentPage.components,
          settings: state.currentPage.settings,
          showLogo: state.currentPage.showLogo,
          backgroundColor: state.currentPage.backgroundColor,
          status: "published",
        });
      } else {
        await updatePageApi(state.currentPage._id, {
          title: state.currentPage.title,
          components: state.currentPage.components,
          settings: state.currentPage.settings,
          showLogo: state.currentPage.showLogo,
          backgroundColor: state.currentPage.backgroundColor,
        });
      }
      toast.success("Page saved successfully");
    } catch (error) {
      console.error("Failed to save page:", error);
      toast.error("Failed to save page");
    }
  }, [state.currentPage]);

  const setCurrentPage = useCallback((page: PageData) => {
    setState((prev) => {
      const exists = prev.allPages.some((p) => p._id === page._id);
      return {
        ...prev,
        currentPage: page,
        allPages: exists
          ? prev.allPages.map((p) => (p._id === page._id ? page : p))
          : [...prev.allPages, page],
        selectedComponent: null,
      };
    });
  }, []);

  return {
    state,
    addComponent,
    duplicateComponent,
    updateComponent,
    deleteComponent,
    selectComponent,
    reorderComponent,
    updatePageSettings,
    updatePageData,
    exportAsJSON,
    exportAsCSS,
    exportAsHTML,
    importFromJSON,
    clearPage,
    updatePageName: handlePageNameChange,
    createNewPage,
    loadPage,
    deletePage,
    updatePage,
    savePage,
    setCurrentPage,
  };
};
